import { useHistory } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import subMonths from 'date-fns/subMonths';
import React, {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Range } from 'react-date-range';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { RowSelectionState } from '@tanstack/react-table';
import Datepicker from '../../components/atoms/Fields/Datepicker';
import { DatepickerPosition } from '../../components/atoms/Fields/Datepicker/enums';
import StatusSelect from '../../components/atoms/Fields/Select/templates/StatusSelect';
import DataTable from '../../components/molecules/Table/dataTable';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import { GetOrdersResponseSingleItem } from '../../setup/apiTypes/orders';
import apiRoutes from '../../setup/consts/apiRoutes';
import routes from '../../setup/consts/routes';
import { ordersTableColumns } from './consts';
import {
  useExportOrders,
  usePlaceholderProps,
  usePrintOrders,
  useStatusOptions,
  useUploadOrdersFormSubmitHandler,
} from './hooks';
import { StyledForm, StyledTableWrapper } from './styles';
import {
  StyledActiveFiltersRow,
  StyledFilterChip,
  StyledFilterDropdownPanel,
  StyledRemovableChip,
  StyledToolbar,
  StyledToolbarActions,
  StyledToolbarFilters,
  StyledToolbarGhostButton,
  StyledToolbarSearch,
} from '../../components/molecules/Toolbar';
import UploadOrderModal from '../../components/organisms/Modal/templates/UploadOrderModal';
import useToggle from '../../hooks/useToggle';
import { DataTableAPI } from '../../components/molecules/Table/types';
import UploadOrderStatusModal from '../../components/organisms/Modal/templates/UploadOrderStatusModal';
import { utcToLocal } from '../../utils/date';
import ConfirmationModal from '../../components/organisms/Modal/templates/ConfirmationModal';
import { missingProfileFeesModalMessages } from './orderFormPage/components/OrderForm/consts';
import Input from '../../components/atoms/Fields/Input';
import { FieldMode } from '../../components/atoms/Fields/enums';
import {
  IconClose,
  IconDownload,
  IconFilter,
  IconMapPin,
  IconPlus,
  IconSearch,
  IconUpload,
} from '../../assets/svg/icons';
import Spinner from '../../components/atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../components/atoms/Spinner/enums';
import { StyledClearButton } from '../../components/atoms/Fields/Autocomplete/styles';
import useUserContext from '../../hooks/useUserContext';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import ScCard from '../../components/atoms/ScCard';
import Button from '../../components/atoms/Button';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import { useCitiesList } from '../../hooks/useCitiesList';
import { IconChevronDown } from '../../assets/svg/icons';

const defaultRange = {
  startDate: subMonths(new Date(), 1),
  endDate: new Date(),
};
const OrdersPage: React.FC = () => {
  // States
  const [range, setRange] = useState<Range>(defaultRange);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string[]>(
    []
  );
  const [ordersTableAPI, setOrdersTableAPI] = useState<DataTableAPI>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedCity, setSelectedCity] = useState<string>('');

  // Hooks
  const history = useHistory();
  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const { user } = useUserContext();
  const { list: cityOptions } = useCitiesList();
  const selectedCityName = useMemo(
    () => cityOptions.find((o) => o.value === selectedCity)?.label ?? '',
    [cityOptions, selectedCity]
  );

  const isAllowedPrinter = user?.isAllowedPrinter;

  const tableColumns = useMemo(
    () => ordersTableColumns(intl, isAllowedPrinter ?? false),
    [intl, isAllowedPrinter]
  );
  const { options: statusSelectItems } = useStatusOptions();
  const { title, description, buttonLabel, Icon } = usePlaceholderProps();
  const goToCreateOrderPage = useCallback(() => {
    history.push(routes.PLACE_ORDER);
  }, [history]);
  const [isOpen, { toggleOn, toggleOff }] = useToggle();
  const [isCityOpen, { toggle: toggleCity, toggleOff: closeCityDropdown, registerContainerRef: cityContainerRef }] = useToggle();
  const [
    isUploadStatusModalOpen,
    {
      toggleOn: toggleUploadStatusModalOn,
      toggleOff: toggleUploadStatusModalOff,
    },
  ] = useToggle();
  const [
    isMissingProfileFeesModalOpen,
    { toggleOn: toggleMissingProfileFeesModalOn },
  ] = useToggle();
  const { handleUploadOrders, taskId } = useUploadOrdersFormSubmitHandler(
    toggleOff,
    toggleUploadStatusModalOn
  );
  const [debouncedQuery] = useDebounce(searchQuery, 400);
  const { handleDownloadOrdersExcelFile, isLoading: exportLoading } =
    useExportOrders();

  const { handlePrintOrders, isLoading: printLoading } = usePrintOrders();
  const [
    exportConfirmationModalIsOpen,
    { toggle: toggleExportConfirmationModal },
  ] = useToggle();

  // Handlers
  const handleEntityHasNoProfileFees = useCallback(() => {
    toggleOff();
    toggleMissingProfileFeesModalOn();
  }, [toggleOff, toggleMissingProfileFeesModalOn]);

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.orders',
        defaultMessage: 'Orders',
      })
    );
  }, [intl, setHeaderTitle]);

  const handleSearchQueryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearchQuery = () => {
    setSearchQuery('');
  };

  const onClearFiltersButtonClick = () => {
    clearSearchQuery();
    setRange(defaultRange);
    setSelectedCity('');
  };

  const onChangeSelectedStatusFilter = (value: string) => {
    let newValues = [];

    if (selectedStatusFilter.includes(value)) {
      newValues = selectedStatusFilter.filter((item) => item !== value);
    } else {
      newValues = [...selectedStatusFilter, value];
    }

    setSelectedStatusFilter(newValues);
  };

  const onExport = () => {
    if (exportConfirmationModalIsOpen) {
      toggleExportConfirmationModal();
    }

    handleDownloadOrdersExcelFile({
      statuses: selectedStatusFilter,
      from: range.startDate && utcToLocal(range.startDate).toISOString(),
      to: range.endDate && utcToLocal(range.endDate).toISOString(),
      page: ordersTableAPI?.page || 1,
      pageSize: ordersTableAPI?.pageSize || 10,
      search: debouncedQuery,
    });
  };

  const onPressPrintButton = () => {
    const ordersIds = Object.keys(rowSelection);
    handlePrintOrders(ordersIds);
  };

  const resetRowSelection = () => {
    setRowSelection({});
  };

  const totalCount = ordersTableAPI?.totalCount;

  const hasActiveFilters =
    selectedStatusFilter.length > 0 ||
    range.startDate?.toDateString() !== defaultRange.startDate.toDateString() ||
    range.endDate?.toDateString() !== defaultRange.endDate.toDateString() ||
    !!searchQuery ||
    !!selectedCity;

  const clearAllFilters = () => {
    setSelectedStatusFilter([]);
    onClearFiltersButtonClick();
    resetRowSelection();
  };

  return (
    <>
      <ScPageHeader
        title={intl.formatMessage({ id: 'mainNavigation.orders' })}
        subtitle={
          totalCount !== undefined
            ? intl.formatMessage({ id: 'orders.subtitle' }, { count: totalCount.toLocaleString() })
            : intl.formatMessage({ id: 'orders.subtitleLoading' })
        }
        actions={
          <>
            <Button
              kind={ButtonKind.Outline}
              icon={IconDownload}
              onClick={() => {
                if (ordersTableAPI?.totalCount && ordersTableAPI.totalCount > 2000) {
                  toggleExportConfirmationModal();
                  return;
                }
                onExport();
              }}
              disabled={exportLoading || ordersTableAPI?.isEmpty}
            >
              <FormattedMessage id='downloadXl' defaultMessage='Export' />
            </Button>
            <Button kind={ButtonKind.Outline} icon={IconUpload} onClick={toggleOn}>
              <FormattedMessage id='orders.uploadOrders' defaultMessage='Upload' />
            </Button>
            <Button kind={ButtonKind.Primary} icon={IconPlus} onClick={goToCreateOrderPage} data-tour='new-order'>
              <FormattedMessage id='orders.createNewOrder' defaultMessage='New order' />
            </Button>
          </>
        }
      />
      {exportConfirmationModalIsOpen ? (
        <ConfirmationModal
          title={intl.formatMessage({ id: 'exportConfirmation' })}
          confirmButtonLabel={intl.formatMessage({ id: 'export' })}
          description={intl.formatMessage({
            id: 'ordersExportConfirmationDescription',
          })}
          cancelButtonLabel={intl.formatMessage({ id: 'cancel' })}
          withCloseButton
          onConfirmButtonClick={onExport}
          onClose={toggleExportConfirmationModal}
          loadingConfirm={exportLoading}
        />
      ) : null}

      <ScCard pad={false} style={{ padding: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
      <StyledToolbar style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            {/* Search */}
            <StyledToolbarSearch>
              <StyledForm onSubmit={(e) => e.preventDefault()}>
                <Input
                  mode={FieldMode.Tertiary}
                  placeholder={intl.formatMessage({
                    id: 'search.searchOrders',
                    defaultMessage: 'Search receipt, customer, phone…',
                  })}
                  isRounded
                  prefixComponent={<IconSearch />}
                  suffixComponent={
                    (ordersTableAPI?.loading && searchQuery) ||
                    (debouncedQuery && !searchQuery) ? (
                      <Spinner
                        mode={SpinnerMode.Dark}
                        size={SpinnerSize.ExtraSmall}
                      />
                    ) : debouncedQuery ? (
                      <StyledClearButton
                        type='button'
                        aria-label='Clear search'
                        onClick={clearSearchQuery}
                      >
                        <IconClose />
                      </StyledClearButton>
                    ) : null
                  }
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  type='input'
                  disabled={ordersTableAPI?.loading}
                />
              </StyledForm>
            </StyledToolbarSearch>

            {/* Filters */}
            <StyledToolbarFilters>
              {/* Status filter chip */}
              <StatusSelect
                placeholder='Status'
                options={statusSelectItems}
                value={selectedStatusFilter}
                onChange={onChangeSelectedStatusFilter}
                renderTrigger={({ toggle, isOpen: statusIsOpen }) => (
                  <StyledFilterChip
                    type='button'
                    onClick={toggle}
                    aria-expanded={statusIsOpen}
                  >
                    <span className='fc-icon'><IconFilter /></span>
                    <span className='fc-label'>{intl.formatMessage({ id: 'orders.statusFilter' })}</span>
                    {selectedStatusFilter.length > 0 && (
                      <span className='fc-badge'>
                        {selectedStatusFilter.length}/{statusSelectItems.length}
                      </span>
                    )}
                    <span className='fc-chevron'><IconChevronDown /></span>
                  </StyledFilterChip>
                )}
              />

              {/* City filter chip */}
              <div ref={cityContainerRef} style={{ position: 'relative' }}>
                <StyledFilterChip
                  type='button'
                  onClick={toggleCity}
                  aria-expanded={isCityOpen}
                >
                  <span className='fc-icon'><IconMapPin /></span>
                  <span className='fc-label'>{intl.formatMessage({ id: 'orders.city' })}</span>
                  <span className='fc-badge'>{selectedCityName || intl.formatMessage({ id: 'orders.all' })}</span>
                  <span className='fc-chevron'><IconChevronDown /></span>
                </StyledFilterChip>
                {isCityOpen && (
                  <StyledFilterDropdownPanel>
                    <button
                      type='button'
                      aria-selected={!selectedCity}
                      onClick={() => { setSelectedCity(''); closeCityDropdown(); }}
                    >
                      {intl.formatMessage({ id: 'orders.allCities' })}
                    </button>
                    {cityOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type='button'
                        aria-selected={selectedCity === opt.value}
                        onClick={() => { setSelectedCity(opt.value); closeCityDropdown(); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </StyledFilterDropdownPanel>
                )}
              </div>

              <Datepicker
                value={range}
                onChange={(item) => setRange(item)}
                position={DatepickerPosition.Right}
              />

            </StyledToolbarFilters>

            {isAllowedPrinter && (
              <StyledToolbarActions>
                <StyledToolbarGhostButton
                  type='button'
                  disabled={printLoading || !Object.keys(rowSelection).length}
                  onClick={onPressPrintButton}
                >
                  <FormattedMessage id='print' defaultMessage='Print' />
                  {printLoading ? (
                    <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.ExtraSmall} />
                  ) : null}
                </StyledToolbarGhostButton>
              </StyledToolbarActions>
            )}
      </StyledToolbar>

      {/* Active filters row — tokens for each applied filter */}
      {hasActiveFilters && (
        <StyledActiveFiltersRow>
          <span className='active-eyebrow'>{intl.formatMessage({ id: 'orders.activeFilters' })}</span>
          {selectedStatusFilter.map((status) => (
            <StyledRemovableChip
              key={status}
              type='button'
              onClick={() =>
                setSelectedStatusFilter((prev) => prev.filter((s) => s !== status))
              }
            >
              {statusSelectItems.find((o) => o.value === status)?.label ?? status}
              <IconClose />
            </StyledRemovableChip>
          ))}
          {selectedCity && (
            <StyledRemovableChip type='button' onClick={() => setSelectedCity('')}>
              {selectedCityName}
              <IconClose />
            </StyledRemovableChip>
          )}
          {searchQuery && (
            <StyledRemovableChip type='button' onClick={clearSearchQuery}>
              "{searchQuery}"
              <IconClose />
            </StyledRemovableChip>
          )}
          <button
            className='clear-all-btn'
            type='button'
            onClick={clearAllFilters}
          >
            {intl.formatMessage({ id: 'orders.clearAll' })}
          </button>
        </StyledActiveFiltersRow>
      )}

      </ScCard>
      <ScCard pad={false}>
      <StyledTableWrapper>
        <DataTable<GetOrdersResponseSingleItem>
          endpoint={apiRoutes.ORDERS}
          tableAPIRef={setOrdersTableAPI}
          rowSelection={rowSelection}
          setSelectedRows={setRowSelection}
          getRowId={(row) => row.id}
          placeholderProps={{
            title,
            description,
            buttonLabel,
            icon: Icon,
            onClick: goToCreateOrderPage,
          }}
          columns={tableColumns}
          query={{
            ...(range.startDate
              ? { from: utcToLocal(range.startDate).toISOString() }
              : {}),
            ...(range.endDate
              ? { to: utcToLocal(range.endDate).toISOString() }
              : {}),
            ...(debouncedQuery ? { search: debouncedQuery } : {}),
            ...(selectedCityName ? { city: selectedCityName } : {}),
            statuses: selectedStatusFilter,
          }}
          onRowClick={({ original }) => history.push(routes.ORDER_DETAIL.replace(':orderId', original.id))}
          queryOptions={{
            cacheTime: 0,
          }}
        />
        <AnimatePresence>
          {isOpen && (
            <UploadOrderModal
              uploadOrders={handleUploadOrders}
              isPopup
              onClose={toggleOff}
              onEntityHasNoProfileFees={handleEntityHasNoProfileFees}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isMissingProfileFeesModalOpen && (
            <ConfirmationModal
              title={intl.formatMessage(missingProfileFeesModalMessages.title)}
              description={intl.formatMessage(
                missingProfileFeesModalMessages.description
              )}
              confirmButtonLabel={intl.formatMessage(
                missingProfileFeesModalMessages.confirmButtonLabel
              )}
              onConfirmButtonClick={() => history.push(routes.CONTACT_US)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isUploadStatusModalOpen && (
            <UploadOrderStatusModal
              {...{ taskId }}
              onClose={toggleUploadStatusModalOff}
              onUploadCompleted={ordersTableAPI?.refetch}
              closeButtonLabel={intl.formatMessage({
                id: 'forms.buttons.close',
                defaultMessage: 'Close',
              })}
            />
          )}
        </AnimatePresence>
      </StyledTableWrapper>
      </ScCard>
    </>
  );
};

export default OrdersPage;
